import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  ArrowRight,
  ArrowDown,
  CheckCircle2,
  ShieldCheck,
  Sun,
  Droplets,
  Wallet,
  PhoneCall,
  Users,
  Clock,
  ExternalLink,
  TrendingDown,
} from 'lucide-react';
import { LOGO_URL } from '../data/mock';
import { LEGAL_ENTITY } from '../components/Footer';
import { ExpertBadge } from '../components/ExpertBadge';

// Solar Victoria deadline: 5pm AEST, Tuesday 30 June 2026 => 07:00 UTC
const DEADLINE_UTC = new Date('2026-06-30T07:00:00Z');
const SOLAR_VIC_SOURCE =
  'https://www.solar.vic.gov.au/solar-homes-program-continuing-provide-targeted-assistance';

const useCountdown = (target) => {
  const calc = () => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      expired: false,
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return time;
};

const CountdownUnit = ({ value, label, testid }) => (
  <div
    className="flex flex-col items-center bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl px-3 py-3 sm:px-5 sm:py-4 min-w-[68px] sm:min-w-[88px]"
    data-testid={testid}
  >
    <span className="text-3xl sm:text-5xl font-black tabular-nums leading-none text-white">
      {String(value).padStart(2, '0')}
    </span>
    <span className="mt-1.5 text-[10px] sm:text-xs uppercase tracking-widest font-bold text-orange-200">
      {label}
    </span>
  </div>
);

const RebateChangesPage = () => {
  const { days, hours, minutes, seconds, expired } = useCountdown(DEADLINE_UTC);

  const scrollToWhat = () => {
    document.getElementById('what-is-changing')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="rebate-changes-page">
      {/* ============ Slim Header ============ */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" data-testid="rc-logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CompareYourOptions.com.au" className="h-8 w-auto" />
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-100 px-3 py-1 rounded-full">
            <AlertTriangle className="w-3.5 h-3.5" />
            Time-sensitive rebate update
          </span>
        </div>
      </header>

      {/* ============ HERO (urgency) ============ */}
      <section
        className="relative overflow-hidden bg-gray-950 text-white"
        data-testid="rc-hero"
      >
        {/* decorative */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 bg-orange-500/20 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 pt-12 pb-14 sm:pt-16 sm:pb-20 text-center">
          {/* Alert badge */}
          <div className="inline-flex items-center gap-2 bg-red-500/15 border border-red-400/30 text-red-200 px-4 py-1.5 rounded-full">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-bold tracking-wide">
              Solar Homes Program rebate rules are changing
            </span>
          </div>

          {/* Headline */}
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Rebate eligibility for
            <br />
            <span className="text-cyo-orange">Solar &amp; Hot Water</span> is
            <br className="hidden sm:block" /> tightening on 1 July 2026
          </h1>

          <p className="mt-6 text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            The Victorian Government is lowering the household income cap for Solar PV and
            heat pump hot water rebates from{' '}
            <span className="font-bold text-white">$210,000</span> to{' '}
            <span className="font-bold text-white">$150,000</span> a year. If your household
            sits in between, you could miss out — unless you apply in time.
          </p>

          {/* Countdown */}
          <div className="mt-9">
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-orange-300 flex items-center justify-center gap-2">
              <CalendarClock className="w-4 h-4" />
              {expired ? 'Application window has closed' : 'Apply before 5pm, Tue 30 June 2026'}
            </p>
            {!expired ? (
              <div className="mt-4 flex items-center justify-center gap-2.5 sm:gap-4">
                <CountdownUnit value={days} label="Days" testid="rc-cd-days" />
                <CountdownUnit value={hours} label="Hours" testid="rc-cd-hours" />
                <CountdownUnit value={minutes} label="Mins" testid="rc-cd-mins" />
                <CountdownUnit value={seconds} label="Secs" testid="rc-cd-secs" />
              </div>
            ) : (
              <p className="mt-4 text-gray-300">
                Speak to our team to check what rebates you may still be eligible for.
              </p>
            )}
          </div>

          {/* CTAs */}
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/compare"
              data-testid="rc-hero-cta-eligibility"
              className="inline-flex items-center justify-center gap-2 bg-cyo-orange hover:bg-orange-600 text-white font-bold px-7 py-4 rounded-full shadow-lg shadow-orange-900/30 hover:-translate-y-0.5 transition-all"
            >
              Check My Eligibility Now
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={scrollToWhat}
              data-testid="rc-hero-cta-learn"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/25 text-white font-bold px-7 py-4 rounded-full hover:bg-white/20 transition-all"
            >
              See what&apos;s changing
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>

          <p className="mt-5 text-xs text-gray-400 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            100% free, no-obligation eligibility check
          </p>
          <div className="mt-6 flex justify-center">
            <ExpertBadge variant="dark" />
          </div>
        </div>
      </section>

      {/* ============ WHAT IS CHANGING ============ */}
      <section
        id="what-is-changing"
        className="max-w-5xl mx-auto px-4 py-14 sm:py-20"
        data-testid="rc-what-changing"
      >
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-cyo-orange px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
            <TrendingDown className="w-3.5 h-3.5" />
            What&apos;s actually changing
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
            The income eligibility cap is
            <br className="hidden sm:block" /> being <span className="text-cyo-orange">lowered</span>
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            From 1 July 2026, the combined household income limit to qualify for Solar PV
            and hot water rebates under the Solar Homes Program is dropping.
          </p>
        </div>

        {/* Before / After */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
          <div
            className="rounded-3xl border border-gray-200 bg-white p-6 sm:p-8 text-center shadow-sm"
            data-testid="rc-cap-current"
          >
            <p className="text-[11px] uppercase tracking-widest font-bold text-emerald-600">
              Now — until 30 June 2026
            </p>
            <p className="mt-3 text-sm text-gray-500">Combined household income cap</p>
            <p className="mt-1 text-4xl sm:text-5xl font-black tabular-nums text-gray-900">
              $210,000
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Wider eligibility
            </span>
          </div>

          <div
            className="rounded-3xl border-2 border-red-200 bg-red-50/40 p-6 sm:p-8 text-center shadow-sm relative"
            data-testid="rc-cap-new"
          >
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-red-500 px-3 py-1 rounded-full">
              From 1 July 2026
            </span>
            <p className="text-[11px] uppercase tracking-widest font-bold text-red-600">
              New rule
            </p>
            <p className="mt-3 text-sm text-gray-500">Combined household income cap</p>
            <p className="mt-1 text-4xl sm:text-5xl font-black tabular-nums text-red-600">
              $150,000
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-100 border border-red-200 px-3 py-1 rounded-full">
              <TrendingDown className="w-3.5 h-3.5" />
              Tighter eligibility
            </span>
          </div>
        </div>

        {/* Deadline strip */}
        <div className="mt-8 max-w-3xl mx-auto bg-gray-950 text-white rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
          <div className="w-12 h-12 rounded-full bg-cyo-orange/20 text-cyo-orange flex items-center justify-center flex-shrink-0">
            <Clock className="w-6 h-6" />
          </div>
          <p className="text-sm sm:text-base text-gray-200">
            To secure a rebate under the current{' '}
            <span className="font-bold text-white">$210,000</span> cap, your application
            must be{' '}
            <span className="font-bold text-cyo-orange">
              submitted in full by 5pm, Tuesday 30 June 2026
            </span>
            .
          </p>
        </div>
      </section>

      {/* ============ ARE YOU AFFECTED ============ */}
      <section
        className="relative bg-gradient-to-b from-orange-50/70 via-white to-white py-14 sm:py-20"
        data-testid="rc-affected"
      >
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-600 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
              <Users className="w-3.5 h-3.5" />
              Are you affected?
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
              If your household earns{' '}
              <span className="text-cyo-orange">$150k–$210k</span>,
              <br className="hidden sm:block" /> this is your window to act
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
              Solar Victoria notes most existing customers will still qualify under the new
              cap — but households in this income band are the ones most likely to miss out
              after 1 July 2026.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 max-w-4xl mx-auto">
            {[
              {
                icon: AlertTriangle,
                tone: 'red',
                title: 'Most at risk',
                body:
                  'Combined income between $150,000 and $210,000 — eligible today, but not from 1 July 2026.',
                testid: 'rc-affected-risk',
              },
              {
                icon: CheckCircle2,
                tone: 'emerald',
                title: 'Likely still eligible',
                body:
                  'Combined income at or under $150,000 — you remain eligible, but rebates and stock are limited, so acting early still pays.',
                testid: 'rc-affected-safe',
              },
              {
                icon: Clock,
                tone: 'orange',
                title: 'Act now to lock it in',
                body:
                  'Applications take time to prepare and submit in full. Starting now gives you the best chance before the deadline.',
                testid: 'rc-affected-act',
              },
            ].map(({ icon: Icon, tone, title, body, testid }) => (
              <div
                key={title}
                data-testid={testid}
                className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 hover:shadow-lg transition-shadow"
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    tone === 'red'
                      ? 'bg-red-50 text-red-500'
                      : tone === 'emerald'
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-orange-50 text-cyo-orange'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 leading-snug">{title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REBATES STILL AVAILABLE ============ */}
      <section className="max-w-5xl mx-auto px-4 py-14 sm:py-20" data-testid="rc-rebates">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
            <Wallet className="w-3.5 h-3.5" />
            Available right now
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
            Rebates you could still claim
          </h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            While the current cap applies, eligible Victorian households can access these
            Solar Homes Program rebates.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
          <div
            className="rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 sm:p-8 shadow-xl"
            data-testid="rc-rebate-solar"
          >
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 text-orange-300 flex items-center justify-center mb-5">
              <Sun className="w-6 h-6" />
            </div>
            <p className="text-xs uppercase tracking-widest font-bold text-orange-300">
              Solar PV (panels)
            </p>
            <p className="mt-2 text-4xl font-black tabular-nums">up to $1,400</p>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              Rebate for eligible owner-occupiers and rental properties, plus an optional
              interest-free loan to help cover the rest.*
            </p>
          </div>

          <div
            className="rounded-3xl bg-gradient-to-br from-gray-900 to-gray-800 text-white p-6 sm:p-8 shadow-xl"
            data-testid="rc-rebate-hotwater"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 text-emerald-300 flex items-center justify-center mb-5">
              <Droplets className="w-6 h-6" />
            </div>
            <p className="text-xs uppercase tracking-widest font-bold text-emerald-300">
              Heat pump hot water
            </p>
            <p className="mt-2 text-4xl font-black tabular-nums">up to $1,400</p>
            <p className="mt-3 text-sm text-gray-300 leading-relaxed">
              Up to $1,400 for eligible locally-made systems, or up to $1,000 for other
              eligible products under the hot water rebate.*
            </p>
          </div>
        </div>

        <p className="mt-6 text-[11px] text-gray-500 leading-relaxed text-center max-w-2xl mx-auto">
          *Rebate amounts, eligibility and availability are set by Solar Victoria and may
          change. Final eligibility depends on your household, property and product. Figures
          shown reflect the Solar Homes Program at the time of writing.
        </p>
      </section>

      {/* ============ WHAT TO DO ============ */}
      <section
        className="relative bg-gradient-to-b from-white to-orange-50/50 py-14 sm:py-20"
        data-testid="rc-steps"
      >
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
              Don&apos;t leave it to the last week
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
              Here&apos;s how to give yourself the best shot at locking in a rebate before
              the rules change.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                title: 'Check your eligibility',
                body:
                  'Tell us your postcode and what you’re considering — solar, hot water or both. It takes a couple of minutes.',
              },
              {
                title: 'Compare your options',
                body:
                  'We’ll help you compare suitable, rebate-friendly products and indicative pricing for your home.',
              },
              {
                title: 'Get your application moving',
                body:
                  'Our team helps you understand the steps so your application can be submitted in full well before 5pm, 30 June 2026.',
              },
            ].map((s, i) => (
              <div
                key={s.title}
                className="flex items-start gap-4 bg-white border border-gray-100 shadow-sm rounded-2xl p-4 sm:p-5"
              >
                <div className="w-10 h-10 rounded-full bg-cyo-orange text-white flex items-center justify-center font-black flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{s.title}</h3>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section
        className="relative bg-gradient-to-br from-cyo-orange to-orange-600 text-white py-14 sm:py-20 overflow-hidden"
        data-testid="rc-final-cta"
      >
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-10 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            The rules change on 1 July 2026.
            <br />
            <span className="text-white">Check your eligibility today.</span>
          </h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto text-sm sm:text-base">
            A quick, free eligibility check could mean the difference between claiming a
            rebate and missing out. There&apos;s no obligation.
          </p>

          {!expired && (
            <div className="mt-7 inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-2 text-sm font-bold">
              <CalendarClock className="w-4 h-4" />
              {days} {days === 1 ? 'day' : 'days'}, {hours} {hours === 1 ? 'hr' : 'hrs'} left to apply
            </div>
          )}

          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/compare"
              data-testid="rc-final-cta-eligibility"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyo-orange font-bold px-7 py-4 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Check My Eligibility Now
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${LEGAL_ENTITY.phone || ''}`}
              data-testid="rc-final-cta-phone"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white font-bold px-7 py-4 rounded-full hover:bg-white/20 transition-all"
            >
              <PhoneCall className="w-4 h-4" />
              Speak to our team
            </a>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-gray-950 text-gray-400 py-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Link to="/" className="inline-block">
            <img
              src={LOGO_URL}
              alt={LEGAL_ENTITY.website}
              className="h-8 w-auto mx-auto brightness-0 invert opacity-90"
            />
          </Link>
          <p className="mt-4 text-xs sm:text-sm">
            {LEGAL_ENTITY.website} is owned and operated by {LEGAL_ENTITY.name} (ABN{' '}
            {LEGAL_ENTITY.abn}).
          </p>
          <p className="mt-2 text-[11px] leading-relaxed max-w-2xl mx-auto text-gray-500">
            Rebate eligibility, amounts, dates and program rules are set by Solar Victoria
            and are subject to change. This page is a general summary to help Victorian
            households act in time and is not financial or eligibility advice. Always confirm
            your eligibility with us or directly with Solar Victoria.
          </p>
          <a
            href={SOLAR_VIC_SOURCE}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rc-source-link"
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-white transition-colors"
          >
            Source: Solar Victoria — Solar Homes Program update
            <ExternalLink className="w-3 h-3" />
          </a>
          <div className="mt-4 flex justify-center gap-4 text-xs">
            <Link to="/privacy" className="hover:text-white">
              Privacy
            </Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-white">
              Terms
            </Link>
            <span>•</span>
            <Link to="/about" className="hover:text-white">
              About
            </Link>
          </div>
          <p className="mt-6 text-[11px] text-gray-600">
            © {new Date().getFullYear()} {LEGAL_ENTITY.website}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RebateChangesPage;
